import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Search, Copy, Check, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";
import Navbar from "@/components/layout/Navbar";
import { LandingFooter } from "@/components/landing/LandingFooter";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import PraiseLogo from "@/components/PraiseLogo";

interface GalleryItem {
  id: string;
  testimonial_id: string;
  output_type: string;
  content: string;
  created_at: string;
  client_name?: string;
}

const Gallery = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [items, setItems] = useState<GalleryItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchGallery();
  }, []);

  const fetchGallery = async () => {
    try {
      const { data: transformations } = await supabase
        .from("transformations")
        .select("id, testimonial_id, output_type, content, created_at")
        .eq("status", "completed")
        .order("created_at", { ascending: false })
        .limit(30);

      if (transformations && transformations.length > 0) {
        const testimonialIds = [...new Set(transformations.map(t => t.testimonial_id))];
        const { data: testimonials } = await supabase
          .from("testimonials")
          .select("id, client_name")
          .in("id", testimonialIds);

        const clientMap = new Map(testimonials?.map(t => [t.id, t.client_name]) || []);

        setItems(
          transformations.map(t => ({
            ...t,
            client_name: clientMap.get(t.testimonial_id) || "Client",
          }))
        );
      }
    } catch (error) {
      console.error("Error fetching gallery:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const filteredItems = items.filter(
    (item) =>
      item.content.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (item.client_name || "").toLowerCase().includes(searchTerm.toLowerCase())
  );

  const copyContent = async (id: string, content: string) => {
    await navigator.clipboard.writeText(content);
    setCopiedId(id);
    toast.success("Copied to clipboard!");
    setTimeout(() => setCopiedId(null), 2000);
  };

  const formatType = (type: string) =>
    type.split("_").map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <Badge variant="outline" className="mb-4">Content Gallery</Badge>
          <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-4">
            Real <span className="gradient-brand-text">Transformations</span>
          </h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Browse AI-transformed testimonials. Copy, customize, and use for inspiration.
          </p>
        </div>

        <div className="flex flex-col sm:flex-row gap-4 mb-8">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search content..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        {isLoading ? (
          <div className="text-center py-12">
            <div className="animate-spin h-8 w-8 border-4 border-brand-500 border-t-transparent rounded-full mx-auto" />
            <p className="text-muted-foreground mt-4">Loading gallery...</p>
          </div>
        ) : filteredItems.length === 0 ? (
          <Card className="border-dashed border-2 border-brand-200">
            <CardContent className="py-12 text-center">
              <PraiseLogo size={48} className="mx-auto mb-4 opacity-30" />
              <h3 className="text-lg font-semibold text-foreground mb-2">No content yet</h3>
              <p className="text-muted-foreground mb-4">Be the first to create a transformation!</p>
              <Link to="/auth">
                <Button className="gradient-brand text-white">Get Started Free</Button>
              </Link>
            </CardContent>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredItems.map((item) => (
              <Card key={item.id} className="border-border hover:shadow-lg transition-all duration-300 group">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <Badge variant="secondary" className="bg-brand-100 text-brand-700">
                      {formatType(item.output_type)}
                    </Badge>
                  </div>
                  <CardTitle className="text-sm text-muted-foreground mt-2">
                    {item.client_name}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="bg-muted p-4 rounded-lg mb-4 max-h-48 overflow-hidden">
                    <pre className="whitespace-pre-wrap text-xs font-sans text-foreground">
                      {item.content.length > 300 ? item.content.slice(0, 300) + "..." : item.content}
                    </pre>
                  </div>
                  <div className="flex items-center justify-end">
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={() => copyContent(item.id, item.content)}>
                        {copiedId === item.id ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                      </Button>
                      <Link to={`/share/${item.testimonial_id}`}>
                        <Button variant="outline" size="sm"><ExternalLink className="h-4 w-4" /></Button>
                      </Link>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        <div className="text-center mt-16 p-8 gradient-brand rounded-2xl">
          <h2 className="text-3xl font-bold text-white mb-4">Create Your Own Transformations</h2>
          <p className="text-white/80 mb-6">Start with 3 free credits — no credit card required</p>
          <Link to="/auth">
            <Button size="lg" className="bg-white text-brand-600 hover:bg-white/90">
              <PraiseLogo size={20} className="mr-2" />
              Get Started Free
            </Button>
          </Link>
        </div>
      </div>
      <LandingFooter />
    </div>
  );
};

export default Gallery;
